// @flow
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import update from "immutability-helper";

import Grid from "@material-ui/core/Grid";

import type { ErrorsType } from "../../../account/types/UserFormType";
import type { StateType, DispatchType } from "../../../global/types/redux";
import {
  ErrosDisplay,
  setErrorMsg,
  WarningDisplay
} from "../../../account/containers/ErrosDisplay";
import { StyledTextField, CancelButton, SubmitButton } from "imagus-components";
import { updateProductPart } from "../../actions/quoteAdminActions";

type UpdateProductPartFormPropsType = {
  onClose: Function,
  actions: { updateProductPart: Function },
  part: { [key: string]: * }
};

type RangeType = {
  price: number,
  minimum: number
};

type UpdateProductPartFormStatesType = {
  errors: ErrorsType,
  loading: boolean,
  warning: boolean,
  price: number,
  minimum: number,
  maximum: number | null,
  description: string,
  notes: string,
  ranges: Array<RangeType>
};

class UpdateProductPartForm extends Component<
  UpdateProductPartFormPropsType,
  UpdateProductPartFormStatesType
> {
  constructor(props: UpdateProductPartFormPropsType) {
    super(props);
    this.state = {
      errors: {},
      loading: false,
      warning: false,
      price: 0,
      minimum: 0,
      maximum: null,
      description: "",
      notes: "",
      ranges: []
    };
  }

  componentDidMount() {
    const part = this.props.part;
    if (part) {
      let initials = { ...this.state };
      initials.price = part.price;
      initials.minimum = part.min;
      initials.maximum = part.max;
      initials.description = part.desctiotion;
      initials.notes = part.notes;

      let ranges = [];
      part.priceRanges.map(
        (range: *, i: number): * => {
          ranges.push({
            minimum: range.node.minimumQuantity,
            price: range.node.price
          });
        }
      );
      initials.ranges = [...ranges];

      this.setState({ ...initials });
    }
  }

  handleTextChange = (e: Event): * => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState({ [name]: value });
  };

  handleQtyChange = (id: number, field: string): * => (e: Event): * => {
    const value = e.target.value;
    const ranges = update(this.state.ranges, {
      [id]: { [field]: { $set: value } }
    });
    this.setState({
      ranges: ranges
    });
  };

  submit = (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();

    if (Object.keys(this.state.errors).length === 0) {
      this.setState((): * => ({ loading: true }));

      this.props.actions.updateProductPart(
        this.props.part.partno,
        this.state.price,
        this.state.minimum,
        this.state.maximum,
        this.state.description,
        this.state.notes,
        this.state.ranges,
        this.submitDone,
        this.submitFail
      );
    }
  };

  submitDone = (billings: Array<*>) => {
    this.setState({
      errors: {},
      warning: true
    });
  };

  submitFail = (errors: ErrorsType): * => {
    this.setState({
      errors: errors,
      loading: false
    });
  };

  render(): React$Element<*> {
    const { errors, loading } = this.state;

    return (
      <form
        onSubmit={(!this.state.loading && this.submit) || undefined}
        className="app-wrapper"
      >
        <div className="app-panel">
          <div className="app-panel-content">
            <Grid container spacing={8}>
              <Grid item xs={12}>
                <StyledTextField
                  isRequired={true}
                  label="Base Price"
                  name="price"
                  value={this.state.price}
                  onChange={this.handleTextChange}
                  inpuType="number"
                />
              </Grid>
              <Grid item xs={6}>
                <StyledTextField
                  isRequired={true}
                  label="Minimum Quantity"
                  name="minimum"
                  value={this.state.minimum}
                  onChange={this.handleTextChange}
                  inpuType="number"
                />
              </Grid>
              <Grid item xs={6}>
                <StyledTextField
                  isRequired={false}
                  label="Maximum Quantity"
                  name="maximum"
                  value={this.state.maximum ? this.state.maximum : ""}
                  onChange={this.handleTextChange}
                  inpuType="number"
                />
              </Grid>
              {this.state.ranges.map(
                (range: *, i: number): * => (
                  <Fragment key={i}>
                    <Grid item xs={6}>
                      <StyledTextField
                        isRequired={true}
                        label="Range Price"
                        name={"range" + i + "price"}
                        value={range.price ? range.price : ""}
                        onChange={this.handleQtyChange(i, "price")}
                        inpuType="number"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StyledTextField
                        isRequired={false}
                        label="Threshold (when >=)"
                        name={"range" + i + "minimum"}
                        value={range.minimum ? range.minimum : ""}
                        onChange={this.handleQtyChange(i, "minimum")}
                        inpuType="number"
                      />
                    </Grid>
                  </Fragment>
                )
              )}
              <Grid item xs={12}>
                <StyledTextField
                  isRequired={true}
                  label="Description"
                  name="description"
                  value={this.state.description}
                  onChange={this.handleTextChange}
                  inpuType="text"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  isRequired={false}
                  label="Notes"
                  name="notes"
                  value={this.state.notes}
                  onChange={this.handleTextChange}
                  inpuType="text"
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <WarningDisplay
                  warning={
                    "Product Part " +
                    this.props.part.partno +
                    " has updated successfully, please refresh page to make quote work correctly."
                  }
                  display={this.state.warning}
                />
              </Grid>
              <Grid item xs={12}>
                <ErrosDisplay errors={this.state.errors} form="close project" />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <div className="form-right-btn-block">
                <CancelButton onClick={this.props.onClose} />
                <SubmitButton
                  loading={this.state.loading}
                  disabled={Object.keys(this.state.errors).length > 0}
                />
              </div>
            </Grid>
          </div>
        </div>
      </form>
    );
  }
}

const mapDispatchToProps = (dispatch: DispatchType): DispatchType => ({
  actions: bindActionCreators({ updateProductPart }, dispatch)
});

export default connect(
  null,
  mapDispatchToProps
)(UpdateProductPartForm);
